<template>
  <div class="app-container">
    <Container
      :title="t('Acceptance.approveAcceptanceHistory')"
      :hasLine="true"
    >
      <template #header>
        <SearchForm
          :formModel="searchFormModel"
          :formStruct="searchFormStruct"
          :hasCreate="false /*$hasPermission(['approvalform_M'])*/"
          :submitButton="t('common.search')"
          :resetButton="t('common.reset')"
          @submit="submitSearch"
          @reset="resetForm"
        />
      </template>
      <template #main>
        <SimpleTable
          ref="tableRef"
          :listLoading="listLoading"
          :tableData="tableData"
          :columnStruct="columnStruct"
          :hadSelect="false"
          :operateColumnWidth="/*$hasPermission(['approvalform_M'])*/ true ? 240:80"
        >
          <template
            #default="slotProps"
          >
            <el-button
              @click="viewDetail(slotProps.row)"
              type="primary"
              size="mini"
            >
              {{ t('Acceptance.viewDetails') }}
            </el-button>
          </template>
        </SimpleTable>
        <el-pagination
          :total="total"
          v-show="total > 0"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :page-size="limit"
          :current-page="page"
          :page-sizes="[10, 20, 50, 100, 200]"
          layout="total, sizes, prev, pager, next, jumper"
          style="text-align: right;"
        />
      </template>
    </Container>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, reactive, ref, toRefs } from 'vue'
import SearchForm from '../../../components/form/search/SearchForm.vue'
import SimpleTable from '../../../components/table/simple/SimpleTable.vue'
import Container from '../../../components/form/container/Container.vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { formatDate, formatThousands } from '@/utils'
import { createModel } from '@/utils/component'
import { getApprovalHistoryPagelist } from '@/apis/accepted-bill/acceptanceReceipt'
import {
  AcceptanceBillApprovalListParamModel,
  AcceptanceBillApprovalPageParamModel,
  ApproveAcceptanceBillHistoryModel
} from '@/model/accepted-bill/approvalFormModel'
import { ElMessage } from 'element-plus'
import { CompanyListParamModel } from '@/model/master-data/companyModel'
import { getCompanyAllList } from '@/apis/main-data/company'

export default defineComponent({
  name: 'AcceptanceApprovalHistoryPageList',
  components: {
    SearchForm,
    SimpleTable,
    Container
  },
  setup() {
    const { t } = useI18n()
    const router = useRouter()
    const tableRef = ref()
    const receiveTypeOptions = [
      { label: t('Acceptance.signForReceipt'), value: 1 },
      { label: t('Acceptance.applicationRejected'), value: 2 }
    ]
    const resultOptions = [
      { label: t('approvalForm.approved'), value: true },
      { label: t('approvalForm.rejected'), value: false }
    ]
    const searchStruct = [
      {
        prop: 'companyCode',
        label: t('company.companyCode'),
        component: 'el-select',
        type: 'text',
        model: '',
        filterable: true,
        loading: false,
        options: [] as any
      },
      {
        prop: 'result',
        label: t('Acceptance.status'),
        placeholder: t('Acceptance.all'),
        component: 'el-select',
        type: 'text',
        model: null,
        options: resultOptions
      },
      {
        prop: 'receiveType',
        label: t('Acceptance.approvalType'),
        placeholder: t('Acceptance.all'),
        component: 'el-select',
        type: 'text',
        model: null,
        options: receiveTypeOptions
      },
      { prop: 'approver', label: t('approvalForm.finalApprover'), component: 'el-input', type: 'text', model: '' }
    ]
    const approveAcceptanceTableStruct = [
      { prop: 'id', label: t('Acceptance.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'submitterName', label: t('Acceptance.Submitter') },
      { prop: 'submitDate', label: t('Acceptance.submitTime'), formatter: (row: ApproveAcceptanceBillHistoryModel) => formatDate(row.submitDate, 'yyyy-MM-dd') },
      { prop: 'approverName', label: t('Acceptance.finalApprover') },
      { prop: 'updatedAt', label: t('Acceptance.operationTime'), formatter: (row: ApproveAcceptanceBillHistoryModel) => formatDate(row.updatedAt, 'yyyy-MM-dd') },
      { prop: 'totalDraftAmt', label: t('Acceptance.draftAmt'), formatter: (row: any) => formatThousands(row.totalDraftAmt), align: 'right' },
      {
        prop: 'receiveType',
        label: t('Acceptance.approvalType'),
        formatter: (row: ApproveAcceptanceBillHistoryModel) => {
          return receiveTypeOptions.find(item => item.value === row.receiveType)?.label
        }
      },
      {
        prop: 'approvalResult',
        label: t('Acceptance.status'),
        formatter: (row: ApproveAcceptanceBillHistoryModel) => {
          return row.approvalResult ? t('approvalForm.approved') : t('approvalForm.rejected')
        }
      }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as AcceptanceBillApprovalListParamModel,
      tableData: Array<ApproveAcceptanceBillHistoryModel>(),
      columnStruct: approveAcceptanceTableStruct,
      listLoading: true,
      limit: 10,
      total: 0,
      page: 1,
      handleCurrentChange(page?: any) {
        dataMap.getPageList(page)
      },
      handleSizeChange(val: any) {
        dataMap.getPageList(null, val)
      },
      async getPageList(page?: any, limit?: any) {
        if (page) {
          dataMap.page = page
        }
        if (limit) {
          dataMap.limit = limit
        }
        dataMap.listLoading = true

        const params: AcceptanceBillApprovalPageParamModel = {
          companyCode: dataMap.searchFormModel.companyCode,
          status: null,
          result: dataMap.searchFormModel.result,
          receiveType: dataMap.searchFormModel.receiveType,
          approver: dataMap.searchFormModel.approver,
          pageCurrent: dataMap.page,
          pageSize: dataMap.limit
        }
        const data = await getApprovalHistoryPagelist(params)
        if (data && data.data && data.data.list.length > 0) {
          dataMap.tableData = data.data.list
        } else {
          dataMap.tableData = []
          if (data?.code !== 200 && data?.msg) {
            ElMessage({
              message: data.msg,
              type: 'error',
              duration: 2000
            })
          }
        }
        dataMap.total = data?.data?.totalCount ?? 0

        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getPageList()
      },
      resetForm() {
        console.log('reset')
      }
    })
    const viewDetail = (row: ApproveAcceptanceBillHistoryModel) => {
      router.push({ path: `acceptanceApprovalHistoryDetail/${row.id}` })
    }
    const getCompanyList = async() => {
      dataMap.searchFormStruct[0].loading = true
      const params: CompanyListParamModel = {
        name: null,
        companyCode: null
      }
      const data = await getCompanyAllList(params)
      const list = data?.data ?? []
      const optionList = []
      for (const iterator of list) {
        optionList.push({
          label: iterator.companyCode + ', ' + iterator.name,
          value: iterator.companyCode
        })
      }
      dataMap.searchFormStruct[0].options = optionList
      dataMap.searchFormStruct[0].loading = false
    }
    onMounted(() => {
      dataMap.getPageList(null, 10)
      getCompanyList()
    })
    return {
      t,
      tableRef,
      viewDetail,
      ...toRefs(dataMap)
    }
  }
})
</script>

<style scoped>

</style>
